import React, { useState, useEffect, useCallback, useRef } from 'react';
import RequestThread from '../components/RequestThread';
import axios from 'axios';
import { useSettings } from '../context/SettingsContext';
import { useAuth } from '../context/AuthContext';

const STATUS_META = {
  pending:  { cls: 'badge-gray',  icon: '⏳' },
  approved: { cls: 'badge-green', icon: '✓'  },
  rejected: { cls: 'badge-red',   icon: '✗'  },
};

function ReviewModal({ request, onClose, onSuccess, t }) {
  const [status,   setStatus]   = useState('approved');
  const [feedback, setFeedback] = useState('');
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');

  const handle = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try {
      await axios.put(`/api/requests/${request._id}/review`, { status, feedback });
      onSuccess();
    } catch (err) { setError(err.response?.data?.message || 'Error'); }
    finally { setLoading(false); }
  };

  const data = request.type === 'create' ? request.requestedData : request.editData;

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 500 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h2 style={{ fontSize: 22, marginBottom: 3 }}>{t('reviewRequest')}</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>{t('requestedBy')}: <strong>{request.partner?.username}</strong>{request.partner?.companyName && ` · ${request.partner.companyName}`}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', color: 'var(--text-muted)', fontSize: 22, lineHeight: 1 }}>×</button>
        </div>

        <div style={{ background: 'var(--surface2)', borderRadius: 'var(--radius-sm)', padding: '14px 16px', marginBottom: 20 }}>
          <div style={{ fontSize: 11, color: 'var(--text-dim)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>
            {request.type === 'create' ? t('createRestaurant') : request.type === 'edit' ? t('editRestaurant') : '🚫 Deactivate'}
            {request.type !== 'create' && request.restaurantId?.name ? ` — ${request.restaurantId.name}` : ''}
          </div>
          {request.type === 'deactivate' ? (
            <div style={{ fontSize: 13, color: 'var(--red)' }}>Partner is requesting deactivation of <strong>{request.restaurantId?.name || '—'}</strong></div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {Object.entries(data || {}).map(([k, v]) => v ? (
                <div key={k}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>{k}</div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{v}</div>
                </div>
              ) : null)}
            </div>
          )}
        </div>

        <form onSubmit={handle}>
          <div className="form-group">
            <label className="form-label">Decision</label>
            <div className="toggle-pill" style={{ width: 'fit-content' }}>
              <button type="button" className={status === 'approved' ? 'active' : ''} onClick={() => setStatus('approved')}>✓ {t('approve')}</button>
              <button type="button" className={status === 'rejected' ? 'active' : ''} onClick={() => setStatus('rejected')}>✗ {t('reject')}</button>
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: 20 }}>
            <label className="form-label">{t('adminFeedback')}</label>
            <textarea rows={3} placeholder={t('adminFeedback')} value={feedback} onChange={e => setFeedback(e.target.value)} style={{ resize: 'none' }} />
          </div>
          {error && <div style={{ background: 'var(--red-bg)', color: 'var(--red)', padding: '10px 14px', borderRadius: 'var(--radius-sm)', fontSize: 13, marginBottom: 14, border: '1px solid rgba(224,92,92,0.2)' }}>{error}</div>}
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={onClose} className="btn-ghost" style={{ flex: 1 }}>{t('cancel')}</button>
            <button type="submit" className="btn-primary" style={{ flex: 2, background: status === 'rejected' ? 'var(--red)' : undefined, color: status === 'rejected' ? '#fff' : undefined }} disabled={loading}>
              {loading ? t('saving') : status === 'approved' ? t('approve') : t('reject')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminRequests() {
  const { t }    = useSettings();
  const { admin } = useAuth();
  const [requests,  setRequests]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [filter,    setFilter]    = useState('pending');
  const [reviewing, setReviewing] = useState(null);
  const [expanded,  setExpanded]  = useState(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const url = filter === 'all' ? '/api/requests' : `/api/requests?status=${filter}`;
      const res = await axios.get(url);
      setRequests(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleExpand = async (req) => {
    const opening = expanded !== req._id;
    setExpanded(opening ? req._id : null);
    if (opening && req.adminUnread > 0) {
      try { await axios.post(`/api/requests/${req._id}/read`); } catch {}
    }
  };

  const typeLabel = (type) => ({ create: t('createRestaurant'), edit: t('editRestaurant'), deactivate: '🚫 Deactivate' }[type] || type);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 style={{ fontSize: 34, marginBottom: 6 }}>{t('allRequests')}</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>{requests.length} requests</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--border)', overflowX: 'auto' }}>
        {[['pending', t('pending')], ['approved', t('approved')], ['rejected', t('rejected')], ['all', t('allRequests')]].map(([key, label]) => (
          <button key={key} onClick={() => setFilter(key)} style={{ background: 'none', whiteSpace: 'nowrap', color: filter === key ? 'var(--accent)' : 'var(--text-muted)', padding: '10px 16px', fontSize: 14, fontWeight: filter === key ? 600 : 400, borderBottom: filter === key ? '2px solid var(--accent)' : '2px solid transparent', marginBottom: -1, transition: 'all 0.15s' }}>{label}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>{t('loading')}</div>
      ) : requests.length === 0 ? (
        <div className="card" style={{ padding: 60, textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
          <div style={{ color: 'var(--text-muted)' }}>No {filter} requests</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {requests.map(req => {
            const meta   = STATUS_META[req.status] || STATUS_META.pending;
            const isOpen = expanded === req._id;
            const data   = req.type === 'create' ? req.requestedData : req.editData;

            return (
              <div key={req._id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px', cursor: 'pointer' }} onClick={() => handleExpand(req)}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 3 }}>
                      <span style={{ fontWeight: 500, fontSize: 14 }}>
                        {typeLabel(req.type)}{req.requestedData?.name ? `: ${req.requestedData.name}` : ''}{req.restaurantId?.name ? `: ${req.restaurantId.name}` : ''}
                      </span>
                      <span className={`badge ${meta.cls}`}>{meta.icon} {t(req.status)}</span>
                      {req.adminUnread > 0 && (
                        <span style={{ background: 'var(--red)', color: '#fff', borderRadius: 10, fontSize: 10, fontWeight: 700, padding: '1px 6px' }}>{req.adminUnread} new</span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                      <span className="badge badge-gold" style={{ fontSize: 11, padding: '2px 8px', marginRight: 8 }}>
                        {req.partner?.username}{req.partner?.companyName ? ` · ${req.partner.companyName}` : ''}
                      </span>
                      {new Date(req.createdAt).toLocaleDateString('en-GB')}
                      {req.messages?.length > 0 && ` · ${req.messages.length} msg`}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {req.status === 'pending' && (
                      <button className="btn-primary" style={{ fontSize: 12, padding: '7px 14px' }} onClick={e => { e.stopPropagation(); setReviewing(req); }}>
                        {t('reviewRequest')}
                      </button>
                    )}
                    <span style={{ color: 'var(--text-dim)', fontSize: 16 }}>{isOpen ? '▲' : '▼'}</span>
                  </div>
                </div>

                {isOpen && (
                  <div style={{ borderTop: '1px solid var(--border)', padding: '16px 20px', background: 'var(--surface2)' }}>
                    {req.type !== 'deactivate' && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 10, marginBottom: 16 }}>
                        {Object.entries(data || {}).map(([k, v]) => v ? (
                          <div key={k}>
                            <div style={{ fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 2 }}>{k}</div>
                            <div style={{ fontSize: 13, fontWeight: 500 }}>{v}</div>
                          </div>
                        ) : null)}
                      </div>
                    )}
                    {req.type === 'deactivate' && (
                      <div style={{ fontSize: 13, color: 'var(--red)', marginBottom: 16 }}>
                        🚫 Requesting deactivation of <strong>{req.restaurantId?.name || '—'}</strong>
                      </div>
                    )}
                    <RequestThread request={req} currentUser={admin} onMessageSent={fetchRequests} t={t} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {reviewing && (
        <ReviewModal
          request={reviewing}
          onClose={() => setReviewing(null)}
          onSuccess={() => { setReviewing(null); fetchRequests(); }}
          t={t}
        />
      )}
    </div>
  );
}
